import React, { useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Pressable, type PressableProps } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	verticalChartLine: {
		content: '',
		display: 'block',
		width: '2px',
		minHeight: '20px',
		marginBottom: token('space.100'),
		borderRadius: token('radius.full'),
	},
	avatarItemContainer: {
		maxWidth: '210px',
		width: '100%',
	},
	avatarItemText: {
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
	missingProfileText: {
		font: token('font.body'),
		color: token('color.text.subtlest'),
	},
	managerAvatarStackWrapper: {
		maxWidth: '450px',
	},
	reportsStackContainer: {
		width: '285px',
		alignContent: 'center',
	},
	tooltipContainer: {
		maxWidth: '269px',
	},
	tooltipWrapper: {
		paddingTop: token('space.200'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.300'),
	},
	reportingLinesChartCard: {
		fontWeight: token('font.weight.regular'),
		display: 'flex',
		flexWrap: 'nowrap',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '285px',
		paddingRight: token('space.200'),
		marginBottom: token('space.100'),
		borderRadius: token('radius.medium'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	reportingLinesChartCardSelected: {
		backgroundColor: token('color.background.neutral'),
		borderStyle: 'solid',
		borderColor: token('color.border.focused'),
		borderWidth: token('border.width.selected'),
	},
	reportingLinesChartCardDisabled: {
		opacity: token('opacity.disabled'),
		cursor: 'not-allowed',
		pointerEvents: 'none',
	},
	reportingLinesChartCardHovered: {
		backgroundColor: token('color.background.neutral.hovered'),
	},
	reportsList: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
		columnGap: token('space.200'),
		justifyItems: 'center',
		width: '100%',
	},
	missingProfileCardStyle: {
		font: token('font.body'),
	},
	avatarOverflowItem: {
		maxWidth: '340px',
	},
	overflowStackCard: {
		display: 'flex',
		flexWrap: 'nowrap',
		alignItems: 'center',
		justifyContent: 'flex-start',
		borderRadius: token('radius.medium'),
		paddingTop: token('space.075'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
		width: '100%',
		marginBottom: token('space.100'),
	},
	overflowStackCardHovered: {
		cursor: 'pointer',
	},
	overflowStackMoreText: {
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		minWidth: '0px',
		display: 'flex',
		flexWrap: 'nowrap',
		alignItems: 'center',
	},
	overflowStackUserName: {
		maxWidth: '350px',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
	collapseButton: {
		width: '100%',
		paddingBottom: token('space.050'),
	},
});

export const VerticalChartLine = (): React.JSX.Element => {
	return <Box xcss={styles.verticalChartLine} backgroundColor="color.background.neutral.hovered" />;
};

export const AvatarItemContainer = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	return <Box xcss={styles.avatarItemContainer}>{children}</Box>;
};

export const AvatarItemText = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
	return <Box xcss={styles.avatarItemText}>{children}</Box>;
};

export const MissingProfileText = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	return <Box xcss={styles.missingProfileText}>{children}</Box>;
};

export const ManagerAvatarStackWrapper = ({
	children,
	testId,
}: {
	children: React.ReactNode;
	testId?: string;
}): React.JSX.Element => {
	return (
		<Box xcss={styles.managerAvatarStackWrapper} testId={testId}>
			{children}
		</Box>
	);
};

export const ReportsStackContainer = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	return <Box xcss={styles.reportsStackContainer}>{children}</Box>;
};

export const TooltipContainer = ({
	children,
	onMouseEnter,
	onMouseLeave,
}: {
	children: React.ReactNode;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
}): React.JSX.Element => {
	return (
		<Box xcss={styles.tooltipContainer} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
			{children}
		</Box>
	);
};

export const TooltipWrapper = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
	return <Box xcss={styles.tooltipWrapper}>{children}</Box>;
};

export const ReportingLinesChartCard = ({
	children,
	selected,
	disabled,
	testId,
	onClick,
	ariaLabel,
}: {
	children: React.ReactNode;
	selected: boolean;
	disabled?: boolean;
	testId?: string;
	onClick?: PressableProps['onClick'];
	ariaLabel?: string;
}): React.JSX.Element => {
	const [hovered, setHovered] = useState(false);
	return (
		<Pressable
			xcss={cx(
				styles.reportingLinesChartCard,
				selected && styles.reportingLinesChartCardSelected,
				disabled && styles.reportingLinesChartCardDisabled,
				hovered && styles.reportingLinesChartCardHovered,
			)}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			testId={testId}
			onClick={onClick}
			aria-label={ariaLabel}
		>
			{children}
		</Pressable>
	);
};

export const ReportsList = ({
	children,
	testId,
}: {
	children: React.ReactNode;
	testId?: string;
}): React.JSX.Element => {
	return (
		<Box xcss={styles.reportsList} testId={testId}>
			{children}
		</Box>
	);
};

export const MissingProfileCardStyle = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	return <Box xcss={styles.missingProfileCardStyle}>{children}</Box>;
};

export const AvatarOverflowItem = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	return <Box xcss={styles.avatarOverflowItem}>{children}</Box>;
};

export const OverflowStackCard = ({
	children,
	onClick,
	onKeyDown,
}: {
	children: React.ReactNode;
	onClick?: PressableProps['onClick'];
	onKeyDown?: PressableProps['onKeyDown'];
}): React.JSX.Element => {
	const [hovered, setHovered] = useState(false);
	return (
		<Pressable
			role="button"
			tabIndex={0}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			xcss={cx(styles.overflowStackCard, hovered && styles.overflowStackCardHovered)}
			onClick={onClick}
			onKeyDown={onKeyDown}
			testId="reporting-lines-overflow-stack-card"
		>
			{children}
		</Pressable>
	);
};

export const OverflowStackMoreText = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	return <Box xcss={styles.overflowStackMoreText}>{children}</Box>;
};

export const OverflowStackUserName = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	return <Box xcss={styles.overflowStackUserName}>{children}</Box>;
};

export const CollapseButton = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
	return <Box xcss={styles.collapseButton}>{children}</Box>;
};
